import React, { useState } from 'react';
import { Link2, Copy, CheckCircle, QrCode, Share2 } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';

const LinkGenerator = () => {
  const [clientName, setClientName] = useState('');
  const [eventType, setEventType] = useState('');
  const [generatedLink, setGeneratedLink] = useState('');
  const [copied, setCopied] = useState(false);
  const [showQR, setShowQR] = useState(false);

  const baseUrl = window.location.origin;

  const generateLink = () => {
    const params = new URLSearchParams();
    if (clientName) params.append('nom', clientName);
    if (eventType) params.append('event', eventType);
    
    const link = `${baseUrl}/avis${params.toString() ? '?' + params.toString() : ''}`;
    setGeneratedLink(link);
    setCopied(false);
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(generatedLink);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const shareViaWhatsApp = () => {
    const message = `Bonjour ${clientName || 'cher client'} ! Merci d'avoir fait confiance à GOS MÉDIA. Nous serions ravis d'avoir votre avis sur nos services. Cliquez sur ce lien : ${generatedLink}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(message)}`, '_blank');
  };

  const getQRCodeUrl = () => {
    return `https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=${encodeURIComponent(generatedLink)}`;
  };

  return (
    <div className="max-w-4xl mx-auto p-6">
      <Card className="shadow-xl">
        <CardContent className="p-8">
          <div className="flex items-center gap-3 mb-6">
            <Link2 className="h-8 w-8 text-amber-600" />
            <h2 className="text-3xl font-bold text-gray-900">Générateur de Lien Personnalisé</h2>
          </div>
          
          <p className="text-gray-600 mb-8">
            Créez un lien personnalisé pour vos clients afin qu'ils puissent laisser facilement un avis.
          </p>

          {/* Form */}
          <div className="space-y-6 mb-8">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Nom du client (optionnel)
              </label>
              <input
                type="text"
                value={clientName}
                onChange={(e) => setClientName(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                placeholder="Jean Kabongo"
              />
              <p className="text-sm text-gray-500 mt-1">
                Le nom sera pré-rempli dans le formulaire
              </p>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Type d'événement (optionnel)
              </label>
              <select
                value={eventType}
                onChange={(e) => setEventType(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
              >
                <option value="">Sélectionnez un type</option>
                <option value="Mariage">Mariage</option>
                <option value="Dot">Dot</option>
                <option value="Conférence">Conférence</option>
                <option value="Festival">Festival</option>
                <option value="Concert">Concert</option>
                <option value="Événement Gouvernemental">Événement Gouvernemental</option>
                <option value="Autre">Autre</option>
              </select>
            </div>

            <Button
              onClick={generateLink}
              className="w-full bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white py-4 rounded-lg font-semibold text-lg shadow-lg"
            >
              <Link2 className="h-5 w-5 mr-2" />
              Générer le lien
            </Button>
          </div>

          {/* Generated Link */}
          {generatedLink && (
            <div className="space-y-6">
              <div className="bg-gray-50 p-6 rounded-lg border-2 border-gray-200">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Lien généré :
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={generatedLink}
                    readOnly
                    className="flex-1 px-4 py-3 bg-white border border-gray-300 rounded-lg text-gray-700 font-mono text-sm"
                  />
                  <Button
                    onClick={copyToClipboard}
                    className={`${
                      copied 
                        ? 'bg-green-500 hover:bg-green-600' 
                        : 'bg-gray-700 hover:bg-gray-800'
                    } text-white px-6`}
                  >
                    {copied ? (
                      <>
                        <CheckCircle className="h-5 w-5 mr-2" />
                        Copié !
                      </>
                    ) : (
                      <>
                        <Copy className="h-5 w-5 mr-2" />
                        Copier
                      </>
                    )}
                  </Button>
                </div>
              </div>

              {/* Actions */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Button
                  onClick={shareViaWhatsApp}
                  className="bg-green-600 hover:bg-green-700 text-white py-4 rounded-lg font-semibold"
                >
                  <Share2 className="h-5 w-5 mr-2" />
                  Partager sur WhatsApp
                </Button>
                
                <Button
                  onClick={() => setShowQR(!showQR)}
                  className="bg-blue-600 hover:bg-blue-700 text-white py-4 rounded-lg font-semibold"
                >
                  <QrCode className="h-5 w-5 mr-2" />
                  {showQR ? 'Masquer' : 'Afficher'} QR Code
                </Button>
              </div>

              {/* QR Code */}
              {showQR && (
                <div className="bg-white p-8 rounded-lg border-2 border-gray-200 text-center">
                  <h3 className="text-lg font-bold text-gray-900 mb-4">QR Code à scanner</h3>
                  <img 
                    src={getQRCodeUrl()} 
                    alt="QR Code" 
                    className="mx-auto mb-4 border-4 border-gray-200 rounded-lg"
                  />
                  <p className="text-sm text-gray-600 mb-4">
                    Scannez ce QR code pour accéder au formulaire d'avis
                  </p>
                  <Button
                    onClick={() => {
                      const link = document.createElement('a');
                      link.href = getQRCodeUrl();
                      link.download = `qr-code-avis-${clientName || 'client'}.png`;
                      link.click();
                    }}
                    className="bg-gray-700 hover:bg-gray-800 text-white"
                  >
                    Télécharger le QR Code
                  </Button>
                </div>
              )}

              {/* Preview */}
              <div className="bg-amber-50 p-6 rounded-lg border-2 border-amber-200">
                <h3 className="text-lg font-bold text-gray-900 mb-3">📱 Comment utiliser ce lien ?</h3>
                <ul className="space-y-2 text-gray-700">
                  <li className="flex items-start gap-2">
                    <span className="text-amber-600">✓</span>
                    <span>Envoyez le lien par WhatsApp, SMS ou email à votre client</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-amber-600">✓</span>
                    <span>Le formulaire s'ouvrira avec les informations pré-remplies</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-amber-600">✓</span>
                    <span>Le client n'aura qu'à donner sa note et écrire son commentaire</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-amber-600">✓</span>
                    <span>L'avis arrivera en attente de modération dans votre dashboard</span>
                  </li>
                </ul>
              </div>
            </div>
          )}

          {/* Info */}
          <div className="mt-8 p-4 bg-blue-50 rounded-lg border border-blue-200">
            <h4 className="font-semibold text-blue-900 mb-2">💡 Astuce</h4>
            <p className="text-sm text-blue-800">
              Vous pouvez aussi utiliser le lien simple <strong>{baseUrl}/avis</strong> sans personnalisation. Le client remplira toutes les informations lui-même.
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default LinkGenerator;
